'use client'
import Link from 'next/link'
import { FaChevronDown, FaExternalLinkAlt, FaUserCircle } from 'react-icons/fa'
import { Settings } from 'lucide-react'
import { memo, useEffect } from 'react'
import { RiLogoutBoxFill } from 'react-icons/ri'
import UserAvatar from '@shared/user/UserAvatar'
import { useHandleLogOut } from '@shared/user/handleLogOut'
import { EVENTS, trackEvent } from 'helpers/analyticsHelper'
import { User } from 'helpers/types'
import { useOrganizationRole } from '@shared/organization-picker'

interface NavLink {
  id: string
  label: string
  href: string
  icon: React.ReactNode
  onClick?: () => void
  external?: boolean
}

const PROFILE_LINK: NavLink = {
  id: 'profile',
  label: 'Profile',
  href: '/dashboard/profile',
  icon: <FaUserCircle />,
  onClick: () => trackEvent(EVENTS.Navigation.Top.AvatarDropdown.ClickProfile),
}

// ENG-10829: hidden for a campaignAdmin (manager), same as
// DashboardMenu.tsx's accountManagementMenuItems.account.
const ACCOUNT_LINK: NavLink = {
  id: 'account',
  label: 'Account Settings',
  href: '/dashboard/account',
  icon: <Settings size={16} />,
}

interface ProfileDropdownProps {
  open: boolean
  toggleCallback: () => void
  user: User
}

const ProfileDropdown = ({
  open,
  toggleCallback,
  user,
}: ProfileDropdownProps): React.JSX.Element => {
  const handleLogOut = useHandleLogOut()
  const organizationRole = useOrganizationRole()
  // ENG-11052: a volunteer gets the same reduced menu as a manager — profile
  // + sign out only, no billing/account-settings.
  const hidesAccountSettings =
    organizationRole === 'campaignAdmin' || organizationRole === 'volunteer'
  const links: NavLink[] = hidesAccountSettings
    ? [PROFILE_LINK]
    : [PROFILE_LINK, ACCOUNT_LINK]

  useEffect(() => {
    if (user) {
      hubspotIntegration(user)
    }
  }, [user])

  const hubspotIntegration = (user: User) => {
    interface HubSpotWindow {
      _hsq?: (string | Record<string, string>)[][]
    }
    const w = window as HubSpotWindow
    const _hsq: (string | Record<string, string>)[][] = (w._hsq = w._hsq || [])
    _hsq.push([
      'identify',
      {
        email: user.email,
        name: `${user.name} ${user.lastName}`,
      },
    ])
  }

  const handleKeyToggle = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || (e.key === 'Escape' && open)) toggleCallback()
  }

  const handleToggle = () => {
    trackEvent(EVENTS.Navigation.Top.ClickAvatarDropdown)
    toggleCallback()
  }

  const handleLogOutClick = (e: React.MouseEvent<HTMLElement>) => {
    trackEvent(EVENTS.Navigation.Top.AvatarDropdown.ClickLogout)
    handleLogOut(e)
  }

  return (
    <div
      className="ml-2 relative cursor-pointer "
      onClick={handleToggle}
      onKeyDown={handleKeyToggle}
      id="nav-run-dropdown"
    >
      <div>
        <div
          role="button"
          tabIndex={0}
          className="flex items-center  rounded-full px-2 py-1 bg-indigo-50"
        >
          <div className="">
            {user?.avatar ? (
              <UserAvatar user={user} size="smaller" />
            ) : (
              <FaUserCircle size={24} />
            )}
          </div>
          <FaChevronDown
            className={`ml-2 mt-[2px] transition-all ${open && 'rotate-180'}`}
          />
        </div>
      </div>

      {open ? (
        <>
          <div
            className="fixed h-screen w-screen top-14 left-0 "
            onClick={toggleCallback}
          />
          <div
            className={`absolute z-50 top-11 right-0 min-w-[270px] bg-secondary text-white rounded-xl  shadow-md transition  ${
              open ? 'p-3 overflow-hidden' : 'p-0 opacity-0 overflow-visible'
            }`}
          >
            {links.map((link) => (
              <Link
                href={link.href}
                id={`nav-${link.id}`}
                key={link.id}
                onClick={link.onClick}
                className="no-underline font-medium block py-3 whitespace-nowrap text-base px-4 rounded flex items-center justify-between"
                rel={link.external ? 'noopener noreferrer nofollow' : ''}
              >
                <div className="flex items-center">
                  {link.icon}
                  <div className="ml-3">{link.label}</div>
                </div>
                {link.external && <FaExternalLinkAlt size={14} />}
              </Link>
            ))}
            <div
              role="link"
              tabIndex={0}
              data-cy="header-link"
              className="block font-medium py-3 whitespace-nowrap text-base px-4 rounded flex items-center justify-between"
              onClick={handleLogOutClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleLogOutClick({
                    preventDefault: e.preventDefault.bind(e),
                    currentTarget: e.currentTarget as HTMLElement,
                  } as React.MouseEvent<HTMLElement>)
                }
              }}
            >
              <div className="flex items-center">
                <RiLogoutBoxFill />
                <div id="nav-log-out" className="ml-3">
                  Logout
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

export default memo(ProfileDropdown)
